import React from 'react';
import styled from 'styled-components';
import { theme, toRem } from 'design';
import { Button } from '../Button';
import { ModalCloseButton, ModalContent, ModalFooter, ModalText } from '../Modal';
import AlertHeader from './AlertHeader';
import { IAlertContentProps } from './AlertProps';

interface IAlertContent extends IAlertContentProps {
  expandMargin?: boolean;
}

const Layout = styled.section<IAlertContent>`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: ${({ maxWidth }) => (maxWidth ? toRem(maxWidth) : toRem(420))};
  height: auto;
  margin: ${toRem(theme.space['4xl'])} 0;
  padding: ${toRem(theme.space['3xl'])};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  p {
    word-break: break-word;
  }

  .modal-text {
    margin-bottom: ${({ expandMargin }) => (expandMargin ? toRem(theme.space.xl) : 0)};
  }
`;

const AlertContent: React.FC<IAlertContentProps> = ({
  title,
  message,
  hideActions,
  hideLeftAction,
  onLeftAction,
  onRightAction,
  leftActionLabel = 'Cancel',
  rightActionLabel = 'Confirm',
  icon,
  iconFill,
  hideIcon,
  maxWidth = 400,
  isLoading = false,
  disabled,
  rightActionAppearance = 'primary',
  leftActionAppearance = 'white',
  centerButtons,
  children,
  onClose,
  dataCy = 'alert-content',
  ...rest
}) => (
  <Layout maxWidth={maxWidth} expandMargin={!!children} data-cy={dataCy} {...rest}>
    {onClose && <ModalCloseButton onClick={onClose} />}
    <AlertHeader icon={icon} iconFill={iconFill} hideIcon={hideIcon} title={title} />

    <ModalContent>
      {message && <ModalText className="modal-text">{message}</ModalText>}
      {children}
    </ModalContent>

    {!hideActions && (
      <ModalFooter columns={hideLeftAction ? 1 : 2} centerButtons={centerButtons} data-cy="modal-footer">
        {!hideLeftAction && <Button data-cy="alert-left-btn" label={leftActionLabel} type="button" appearance={leftActionAppearance} size="large" onClick={onLeftAction} />}
        <Button data-cy="alert-right-btn" label={rightActionLabel} type="button" appearance={rightActionAppearance} size="large" onClick={onRightAction} isProcessing={isLoading} disabled={isLoading || disabled} />
      </ModalFooter>
    )}
  </Layout>
);

export default AlertContent;
